import { useRef, useEffect, useCallback } from "react";
import type { Ball, PhysicsConfig } from "@/lib/physics";
import { step, drawScene } from "@/lib/physics";

interface SimCanvasProps {
	balls: Ball[];
	config: PhysicsConfig;
	width?: number;
	height?: number;
	showTrails?: boolean;
	showVectors?: boolean;
	showEnergy?: boolean;
	onFrame?: (balls: Ball[]) => void;
	onClick?: (x: number, y: number) => void;
	running?: boolean;
}

export function SimCanvas({
	balls,
	config,
	width = 700,
	height = 400,
	showTrails = true,
	showVectors = false,
	showEnergy = false,
	onFrame,
	onClick,
	running = true,
}: SimCanvasProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const animRef = useRef<number>(0);
	const lastTimeRef = useRef<number>(0);
	const ballsRef = useRef<Ball[]>(balls);
	const configRef = useRef<PhysicsConfig>(config);
	const runningRef = useRef<boolean>(running);

	ballsRef.current = balls;
	configRef.current = config;
	runningRef.current = running;

	const animate = useCallback(
		(time: number) => {
			const canvas = canvasRef.current;
			if (!canvas) return;
			const ctx = canvas.getContext("2d");
			if (!ctx) return;

			if (lastTimeRef.current === 0) {
				lastTimeRef.current = time;
			}

			let dt = (time - lastTimeRef.current) / 1000;
			lastTimeRef.current = time;

			// Cap delta time to prevent physics explosions
			dt = Math.min(dt, 0.033);

			if (runningRef.current) {
				// Sub-step for stability
				const subSteps = 3;
				const subDt = dt / subSteps;
				for (let i = 0; i < subSteps; i++) {
					step(ballsRef.current, configRef.current, subDt, width, height);
				}
				onFrame?.(ballsRef.current);
			}

			drawScene(ctx, ballsRef.current, width, height, showTrails, showVectors, showEnergy, configRef.current);

			animRef.current = requestAnimationFrame(animate);
		},
		[width, height, showTrails, showVectors, showEnergy, onFrame],
	);

	useEffect(() => {
		lastTimeRef.current = 0;
		animRef.current = requestAnimationFrame(animate);
		return () => cancelAnimationFrame(animRef.current);
	}, [animate]);

	const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
		if (!onClick) return;
		const rect = canvasRef.current?.getBoundingClientRect();
		if (!rect) return;
		const scaleX = width / rect.width;
		const scaleY = height / rect.height;
		const x = (e.clientX - rect.left) * scaleX;
		const y = (e.clientY - rect.top) * scaleY;
		onClick(x, y);
	};

	return (
		<canvas
			ref={canvasRef}
			width={width}
			height={height}
			onClick={handleClick}
			className="w-full rounded-xl border border-white/10 cursor-crosshair"
			style={{ maxWidth: width, aspectRatio: `${width}/${height}` }}
		/>
	);
}
